import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';
import { setIsMapDataUpdateNeeded } from '@slices/map';

import { removeAssets } from '@api/assets';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IResponseError } from '@interfaces/errors/apiErrors';

import { ESnackbarTypes } from '@enums/common';

import { ASSETS, ASSETS_LIST, DELETE_ASSET, FILTER_THRESHOLDS } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export const useMutateDeleteAsset = (onSettledCallbacks?: Array<() => void>) => {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		mutate: handleDeleteAsset,
		isLoading: isLoadingDeleteAsset,
	} = useMutation<string>({
		mutationKey: [DELETE_ASSET],
		mutationFn: (id: string) => removeAssets({
			ids: [id],
			isAll: false,
			filter: null,
		}),
		onSuccess: (data) => {
			const isAssetDeleted = data?.countNotDeleted !== 1;

			if (onSettledCallbacks?.length) {
				onSettledCallbacks.forEach((callback) => callback());
			}

			dispatch(setIsMapDataUpdateNeeded(true));

			dispatch(setSnackbar({
				message: isAssetDeleted ? responseMessages.removedAsset : responseMessages.assetNotRemoved,
				type: isAssetDeleted ? ESnackbarTypes.success : ESnackbarTypes.error,
			}));

			return [
				queryClient.invalidateQueries([ASSETS]),
				queryClient.invalidateQueries([ASSETS_LIST]),
				queryClient.invalidateQueries([FILTER_THRESHOLDS]),
			];
		},
		onError: (err: IResponseError) => {
			if (onSettledCallbacks?.length) {
				onSettledCallbacks.forEach((callback) => callback());
			}

			handleErrorFromMessage(err);
		},
	});

	return { handleDeleteAsset, isDeletingAsset: isLoadingDeleteAsset };
};
